import { useState } from 'react';
import {
  AlertBannerType,
  AlertBannerVariant,
  ExAlertBanner,
  ExIconButton, ExLink, IconButtonFlavor, IconButtonSize, IconButtonType,
  LinkSize,
} from '@boomi/exosphere';
import { useTranslation } from 'react-i18next';
import { generateUniqueKey } from '@services/agent-foundation/agent-foundation-api-service';
import '../PreviewSection.scss';

function TaskSection({
  template,
  sources,
  handleStepClick,
  showEdit,
  showToolBanner,
  isAgentAlreadyCreated = false,
  isReadyToUseTemplate = false,
}: any) {
  const [t] = useTranslation('common');
  const [expandedTasks, setExpandedTasks] = useState<{
    [key: string]: boolean;
  }>({});
  const toggleTaskDetails = (taskId: string) => {
    setExpandedTasks((prev: any) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };
  const totalTools = template?.tasks?.reduce(
    (sum: number, task: any) => sum + (task.tools?.length || 0),
    0,
  );
  const mcpCount = sources?.length ?? 0;

  let toolsText = t('import_agent_tools_auto_created');

  if (totalTools === 1) {
    toolsText = t('import_agent_one_tool_auto_created');
  }
  if (isAgentAlreadyCreated && totalTools === 1) {
    toolsText = t('templates_one_tool_auto_created');
  }
  if (isAgentAlreadyCreated && totalTools > 1) {
    toolsText = t('templates_tools_auto_created');
  }
  if (isReadyToUseTemplate && totalTools === 1) {
    toolsText = t('templates_one_tool_auto_deployed');
  }
  if (isReadyToUseTemplate && totalTools > 1) {
    toolsText = t('templates_tools_auto_deployed');
  }
  return (
    <>
      {showToolBanner && totalTools > 0 && (
        <ExAlertBanner
          className="tools-banner"
          type={AlertBannerType.INFO}
          open
          variant={AlertBannerVariant.SUBTLE}
        >
          {mcpCount > 0 && `${mcpCount} ${mcpCount > 1 ? t('import_agent_mcp_source_auto_created') : t('import_agent_one_mcp_source_auto_created')} `}
          {totalTools}
          {' '}
          {toolsText}
        </ExAlertBanner>
      )}
      <div className="review-agent-container">
        {showEdit && (
          <div className="preview-button-container">
            <div className="agent-title">{t('review_panel_task_title')}</div>
            <ExIconButton
              icon="pencil"
              type={IconButtonType.TERTIARY}
              flavor={IconButtonFlavor.BASE}
              size={IconButtonSize.SMALL}
              tooltipText="Edit"
              onClick={(e: any) => {
                handleStepClick(e, 'Tasks');
              }}
            />
          </div>
        )}
        <div className="agent-task-title">{t('tasks_panel_tasks_tab_title')}</div>
        {template?.tasks?.length > 0 ? (
          template?.tasks?.map((task: any) => (
            <div className="review-agent-section task-section" key={task.id}>
              <div className="info-row">
                <div className="info-key">{t('name')}</div>
                <div className="info-value">{task?.name}</div>
              </div>
              <div className="info-row">
                <div className="info-key">{t('description')}</div>
                <div className="info-value">{task?.description || task?.objective || '\u2013'}</div>
              </div>
              <div className="info-row">
                <div className="info-key">{t('instructions')}</div>
                <div className="info-value">
                  {task?.instructions?.length > 0 && (() => {
                    const instructionsArray = task.instructions[0]
                      .split('\n')
                      .map((line: string) => line.trim())
                      .filter((line: string) => line !== '');
                    return (
                      <ul>
                        {instructionsArray.map((instruction: string, index: number) => (
                          <li key={generateUniqueKey(index)}>{instruction}</li>
                        ))}
                      </ul>
                    );
                  })()}
                  {task?.instructions?.length < 1 && (
                    <div className="info-value">{'\u2013'}</div>
                  )}
                </div>
              </div>
              <div className="info-row">
                <div className="info-key">{t('tools')}</div>
                <div className="info-value">
                  {task?.tools?.length > 0 && (
                    <div>
                      <div className="tools-count">
                        {`${task?.tools?.length} ${
                          task?.tools?.length === 1 ? 'Tool' : 'Tools'
                        }`}
                      </div>
                      <ExLink
                        onClick={(e: any) => {
                          e.stopPropagation();
                          toggleTaskDetails(task.id);
                        }}
                        className="details-link"
                        rel="noreferrer"
                        size={LinkSize.XSMALL}
                      >
                        {expandedTasks[task.id]
                          ? 'Hide details'
                          : 'Show details'}
                      </ExLink>
                      {expandedTasks[task.id] && (
                        <div className="tools-details">
                          <ul>
                            {task?.tools.map((tool: any, index: number) => (
                              <li key={generateUniqueKey(index)}>
                                {`${
                                  tool.name || (tool.type === 'OpenAPI' ? 'API' : tool.type)
                                } (${tool.type === 'OpenAPI' ? 'API' : tool.type})`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  {task?.tools?.length < 1 && (
                    <div className="info-value">{'\u2013'}</div>
                  )}
                </div>
              </div>
              <div className="info-row">
                <div className="info-key">{t('metahub_glossaries')}</div>
                <div className="info-value">
                  {task?.metahubGlossaries?.length > 0 && (() => {
                    const metahubGlossaries = task.metahubGlossaries || [];
                    return (
                      <ul>
                        {metahubGlossaries.map((glossary: any, index: number) => (
                          <li key={generateUniqueKey(index)}>{glossary?.name}</li>
                        ))}
                      </ul>
                    );
                  })()}
                  {task?.metahubGlossaries?.length < 1 && (
                    <div className="info-value">{'\u2013'}</div>
                  )}
                </div>
              </div>
              <div className="info-row">
                <div className="info-key">{t('knowledge_hub_kb')}</div>
                <div className="info-value">
                  {task?.knowledgeBases?.length > 0 && (() => {
                    const knowledgeBases = task.knowledgeBases || [];
                    return (
                      <ul>
                        {knowledgeBases.map((kb: any, index: number) => (
                          <li key={generateUniqueKey(index)}>{kb?.name}</li>
                        ))}
                      </ul>
                    );
                  })()}
                  {task?.knowledgeBases?.length < 1 && (
                    <div className="info-value">{'\u2013'}</div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="review-agent-section task-section">
            <div className="info-row">
              <div className="info-key">{t('name')}</div>
              <div className="info-value">{'\u2013'}</div>
            </div>
            <div className="info-row">
              <div className="info-key">{t('description')}</div>
              <div className="info-value">{'\u2013'}</div>
            </div>
            <div className="info-row">
              <div className="info-key">{t('instructions')}</div>
              <div className="info-value">{'\u2013'}</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default TaskSection;
